import { useRef, useState } from "react";
import { Upload, Link2, X, Loader2, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Image upload field — accepts either a drag/drop/click file upload OR a pasted URL.
 * Resolves to a single URL stored in `value`. Designed for cover images.
 *
 *   <ImageUploadField value={form.cover_image} onChange={(url) => setForm({...form, cover_image: url})} />
 */
export function ImageUploadField({ value, onChange, label = "Cover image", testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("upload"); // "upload" | "url"
  const [urlInput, setUrlInput] = useState("");
  const [dragging, setDragging] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please upload an image file (jpg, png, webp).");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image too large — max 12 MB");
      return;
    }
    setUploading(true);
    setPreviewError(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Build absolute URL so it works in canvas, social shares, emails, everywhere
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const absoluteUrl = `${apiBase}${data.url}`;
      onChange(absoluteUrl);
      toast.success(`Uploaded (${Math.round(data.size / 1024)} KB)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const setFromUrl = () => {
    const u = (urlInput || "").trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    onChange(u);
    setUrlInput("");
    setPreviewError(false);
    toast.info("URL set. If the preview is blank below, the host probably blocks hotlinking — use the Upload tab instead.");
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</span>
        <div className="flex gap-0.5 text-[10px]">
          <button type="button" onClick={() => setMode("upload")}
            className={`px-2 py-0.5 uppercase tracking-widest font-bold transition-colors ${mode === "upload" ? "text-white bg-[#FF331F]" : "text-zinc-500 bg-[#0F0F0F] hover:text-white"}`}
            data-testid={`${testid}-tab-upload`}>
            <Upload className="h-2.5 w-2.5 inline mr-1" /> Upload
          </button>
          <button type="button" onClick={() => setMode("url")}
            className={`px-2 py-0.5 uppercase tracking-widest font-bold transition-colors ${mode === "url" ? "text-white bg-[#FF331F]" : "text-zinc-500 bg-[#0F0F0F] hover:text-white"}`}
            data-testid={`${testid}-tab-url`}>
            <Link2 className="h-2.5 w-2.5 inline mr-1" /> URL
          </button>
        </div>
      </div>

      {mode === "upload" ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
            dragging ? "border-[#FF331F] bg-[#FF331F]/5" : "border-[#27272A] bg-[#050505] hover:border-zinc-600"
          }`}
        >
          <input
            ref={fileRef} type="file" accept="image/*" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); }}
            data-testid={`${testid}-input`}
          />
          <div className="p-6 text-center">
            {uploading ? (
              <Loader2 className="h-6 w-6 text-[#FF331F] mx-auto animate-spin" />
            ) : (
              <Upload className="h-6 w-6 text-zinc-500 mx-auto mb-2" />
            )}
            <div className="text-xs text-zinc-300 font-bold">
              {uploading ? "Uploading…" : "Drop image here or click to choose"}
            </div>
            <div className="text-[10px] text-zinc-600 mt-1">JPG, PNG, WebP up to 12 MB — auto-compressed</div>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/your-image.jpg"
            className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs"
            data-testid={`${testid}-url-input`}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFromUrl(); } }}
          />
          <Button type="button" onClick={setFromUrl} variant="outline" className="border-[#27272A] bg-[#050505] text-white hover:bg-[#1A1A1A] rounded-sm">
            Set
          </Button>
        </div>
      )}

      {/* Preview */}
      {value ? (
        <div className="relative border border-[#27272A] bg-[#050505] overflow-hidden aspect-[16/9] max-w-md">
          {!previewError ? (
            <img
              src={value} alt="preview"
              className="w-full h-full object-cover"
              crossOrigin="anonymous"
              onError={() => setPreviewError(true)}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-amber-300 p-4 text-center">
              <AlertTriangle className="h-6 w-6" />
              <div className="text-xs">Couldn&apos;t load this image. The host may block hotlinking. Try the <strong>Upload</strong> tab instead.</div>
            </div>
          )}
          <button
            type="button" onClick={() => { onChange(""); setPreviewError(false); }}
            className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white p-1 rounded-sm"
            data-testid={`${testid}-clear`}
            title="Remove image"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="text-[10px] text-zinc-600 italic flex items-center gap-1">
          <ImageIcon className="h-3 w-3" /> No image set
        </div>
      )}
    </div>
  );
}

/**
 * Multi-image gallery uploader. Each item is a URL string.
 *
 *   <ImageGalleryField items={form.gallery} onChange={(arr) => setForm({...form, gallery: arr})} />
 */
export function ImageGalleryField({ items = [], onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const newUrls = [];
    for (const f of Array.from(files)) {
      if (!f.type?.startsWith("image/")) continue;
      if (f.size > 12 * 1024 * 1024) {
        toast.error(`${f.name}: too large (max 12 MB)`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(`${apiBase}${data.url}`);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "Failed"}`);
      }
    }
    if (newUrls.length) {
      onChange([...items, ...newUrls]);
      toast.success(`${newUrls.length} image(s) added`);
    }
    setUploading(false);
  };

  const remove = (idx) => {
    const next = [...items]; next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <input ref={fileRef} type="file" accept="image/*" multiple hidden
        onChange={(e) => handleFiles(e.target.files)} data-testid={`${testid}-input`} />
      <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
        variant="outline" className="border-[#27272A] bg-[#050505] text-white hover:bg-[#1A1A1A] rounded-sm font-bold uppercase tracking-widest">
        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        {uploading ? "Uploading…" : "Add photos"}
      </Button>
      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {items.map((src, i) => (
            <div key={src} className="relative aspect-square border border-[#27272A] bg-[#050505] overflow-hidden group">
              <img src={src} alt={`gallery ${i + 1}`} className="w-full h-full object-cover" crossOrigin="anonymous"
                onError={(e) => { e.target.style.opacity = "0.2"; }} />
              <button type="button" onClick={() => remove(i)}
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
